import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import InstallPrompt from "@/components/InstallPrompt";
import { MusicProvider } from "@/context/MusicContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import AuthCallback from "@/pages/AuthCallback";
import Welcome from "@/pages/Welcome";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import HomePage from "@/pages/Home";
import Learn from "@/pages/Learn";
import LessonReader from "@/pages/LessonReader";
import Coach from "@/pages/Coach";
import Journal from "@/pages/Journal";
import Wellness from "@/pages/Wellness";
import Progress from "@/pages/Progress";
import Tools from "@/pages/Tools";
import Community from "@/pages/Community";
import Plans from "@/pages/Plans";
import PlanDetail from "@/pages/PlanDetail";
import Paywall from "@/pages/Paywall";
import SettingsPage from "@/pages/Settings";
import Notifications from "@/pages/Notifications";
import Moderation from "@/pages/Moderation";
import AdminUsers from "@/pages/AdminUsers";
import AdminPromoCodes from "@/pages/AdminPromoCodes";
import AdminAIUsage from "@/pages/AdminAIUsage";
import AdminMemberInsights from "@/pages/AdminMemberInsights";
import Therapists from "@/pages/Therapists";
import AdminTherapists from "@/pages/AdminTherapists";
import TherapistApply from "@/pages/TherapistApply";
import TherapistProfile from "@/pages/TherapistProfile";
import Affirmations from "@/pages/Affirmations";
import Assessment from "@/pages/Assessment";
import AssessmentResults from "@/pages/AssessmentResults";
import Terms from "@/pages/Terms";
import { Toaster } from "@/components/ui/sonner";
import Resources from "@/pages/Resources";
import Features from "@/pages/Features";
import About from "@/pages/About";
import NotFound from "@/pages/NotFound";
import SplashExperience from "@/components/SplashExperience";
import EnterScreen from "@/components/EnterScreen";

function Protected({ children }) {
  return (
    <ProtectedRoute>
      <AppLayout>{children}</AppLayout>
    </ProtectedRoute>
  );
}

// Deterministic robots policy: index public marketing/content pages, noindex
// everything else (private app, funnel, and thin utility pages). This is the
// single source of truth for the robots meta tag; private data stays gated by auth.
const INDEX_EXACT = new Set(["/", "/terms", "/assessment"]);
function computeRobots(pathname) {
  if (INDEX_EXACT.has(pathname)) return "index, follow";
  if (pathname === "/therapists") return "index, follow";
  // Public therapist profiles are indexable; the apply/edit forms are not.
  if (
    pathname.startsWith("/therapists/") &&
    !pathname.startsWith("/therapists/apply") &&
    !pathname.startsWith("/therapists/edit")
  ) {
    return "index, follow";
  }
  return "noindex, nofollow";
}

// Only the production domain is allowed to emit index directives; every other host
// (preview/staging/localhost) is forced to noindex so non-production URLs never enter
// search results. This never blocks production itself.
const PROD_HOSTS = new Set(["sisterhoodplus.com", "www.sisterhoodplus.com"]);
function isProdHost() {
  return typeof window !== "undefined" && PROD_HOSTS.has(window.location.hostname);
}

function useRobotsMeta() {
  const location = useLocation();
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", isProdHost() ? computeRobots(location.pathname) : "noindex, nofollow");
  }, [location.pathname]);
}

function AppRouter() {
  const location = useLocation();
  useRobotsMeta();
  // Detect Google OAuth return synchronously (before ProtectedRoute runs)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Welcome />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/assessment" element={<Assessment />} />
      <Route path="/assessment/results" element={<AssessmentResults />} />
      <Route path="/get-started" element={<Onboarding />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/features" element={<Features />} />
      <Route path="/about" element={<About />} />
      <Route path="/therapists/apply" element={<TherapistApply />} />
      <Route path="/therapists/edit/:token" element={<TherapistApply />} />
      <Route path="/therapists/:id" element={<TherapistProfile />} />
      <Route path="/therapists" element={<Therapists />} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/home" element={<Protected><HomePage /></Protected>} />
      <Route path="/learn" element={<Protected><Learn /></Protected>} />
      <Route path="/learn/:categoryId" element={<Protected><Learn /></Protected>} />
      <Route path="/lesson/:lessonId" element={<Protected><LessonReader /></Protected>} />
      <Route path="/coach" element={<Protected><Coach /></Protected>} />
      <Route path="/journal" element={<Protected><Journal /></Protected>} />
      <Route path="/wellness" element={<Protected><Wellness /></Protected>} />
      <Route path="/progress" element={<Protected><Progress /></Protected>} />
      <Route path="/tools" element={<Protected><Tools /></Protected>} />
      <Route path="/community" element={<Protected><Community /></Protected>} />
      <Route path="/plans" element={<Protected><Plans /></Protected>} />
      <Route path="/plans/:planId" element={<Protected><PlanDetail /></Protected>} />
      <Route path="/paywall" element={<ProtectedRoute><Paywall /></ProtectedRoute>} />
      <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
      <Route path="/moderation" element={<Protected><Moderation /></Protected>} />
      <Route path="/admin/users" element={<Protected><AdminUsers /></Protected>} />
      <Route path="/admin/promo-codes" element={<Protected><AdminPromoCodes /></Protected>} />
      <Route path="/admin/ai-usage" element={<Protected><AdminAIUsage /></Protected>} />
      <Route path="/admin/member-insights" element={<Protected><AdminMemberInsights /></Protected>} />
      <Route path="/affirmations" element={<Protected><Affirmations /></Protected>} />
      <Route path="/resources" element={<Protected><Resources /></Protected>} />
      <Route path="/admin/therapists" element={<Protected><AdminTherapists /></Protected>} />
      <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  // Public, shareable pages (the free therapist directory + the apply link) skip the
  // brand ENTER splash so visitors land straight on the content.
  const publicEntry = typeof window !== "undefined" && /^\/(therapists|features|about)(\/.*)?$/.test(window.location.pathname);
  const [entered, setEntered] = useState(() => publicEntry || !!sessionStorage.getItem("sh_entered"));
  const [splash, setSplash] = useState(() => !publicEntry && !sessionStorage.getItem("sh_splash_seen"));
  return (
    <MusicProvider>
      {!entered && <EnterScreen onEnter={() => { sessionStorage.setItem("sh_entered", "1"); setEntered(true); }} />}
      {entered && splash && <SplashExperience onFinish={() => { sessionStorage.setItem("sh_splash_seen", "1"); setSplash(false); }} />}
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          {entered && <InstallPrompt />}
          <Toaster position="top-center" />
        </AuthProvider>
      </BrowserRouter>
    </MusicProvider>
  );
}

export default App;
